import dayjs from "dayjs";

/** Flag when |unit price − product window average| / average is at least this ratio. */
export const DEVIATION_THRESHOLD = 0.5;
export const MIN_WEEK_SAMPLE = 2;
export const MIN_MONTH_SAMPLE = 3;
export const MISMATCH_RELATIVE_THRESHOLD = 0.1;
export const MISMATCH_ABSOLUTE_THRESHOLD = 0.25;

export function toFiniteNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function productIdFromRow(row) {
  const raw = row?.productId ?? row?.product_id;
  if (raw == null) return "";
  if (typeof raw === "object" && raw.id != null) return String(raw.id);
  return String(raw);
}

export function isVerifiedValue(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

export function matchesVerifiedView(row, view) {
  const verified = isVerifiedValue(row?.human_verified);
  if (view === "voided") return false;
  if (view === "verified") return verified;
  if (view === "unverified") return !verified;
  return true;
}

/** Sunday–Saturday calendar week, matching dayjs `startOf("week")`. */
export function weekKeyFromDate(value) {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "";
  return parsed.startOf("week").format("YYYY-MM-DD");
}

export function monthKeyFromDate(value) {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "";
  return parsed.format("YYYY-MM");
}

function addToWindow(map, key, unitPrice) {
  const current = map.get(key) || { sum: 0, count: 0 };
  current.sum += unitPrice;
  current.count += 1;
  map.set(key, current);
}

function finalizeWindows(map) {
  const averages = new Map();
  for (const [key, windowStats] of map) {
    if (!windowStats.count) continue;
    averages.set(key, {
      avg: windowStats.sum / windowStats.count,
      count: windowStats.count,
    });
  }
  return averages;
}

/**
 * Product-level weekly and monthly unit-price averages from the filtered set.
 * @param {object[]} rows
 */
export function buildProductWindowAverages(rows) {
  const weeks = new Map();
  const months = new Map();

  for (const row of rows || []) {
    const productId = productIdFromRow(row);
    const unitPrice = toFiniteNumber(row.unit_price ?? row.unitPriceValue);
    const recordAt = row.record_at ?? row.recordAtValue;
    if (!productId || unitPrice == null) continue;
    const weekKey = weekKeyFromDate(recordAt);
    const monthKey = monthKeyFromDate(recordAt);
    if (weekKey) {
      addToWindow(weeks, `${productId}|${weekKey}`, unitPrice);
    }
    if (monthKey) {
      addToWindow(months, `${productId}|${monthKey}`, unitPrice);
    }
  }

  return {
    weeks: finalizeWindows(weeks),
    months: finalizeWindows(months),
  };
}

function deviationCheck(unitPrice, windowStats, minSample, threshold) {
  if (!windowStats || windowStats.count < minSample || !(windowStats.avg > 0)) {
    return null;
  }
  const ratio = Math.abs(unitPrice - windowStats.avg) / windowStats.avg;
  if (ratio < threshold) return null;
  return {
    avg: windowStats.avg,
    unitPrice,
    sampleSize: windowStats.count,
    percent: ratio * 100,
    above: unitPrice >= windowStats.avg,
  };
}

function mismatchCheck(row) {
  const unitPrice = toFiniteNumber(row.unit_price ?? row.unitPriceValue);
  const price = toFiniteNumber(row.price ?? row.priceValue);
  const quantity = toFiniteNumber(row.unit_quantity ?? row.unitQuantityValue);
  if (unitPrice == null || price == null || quantity == null || quantity <= 0 || price <= 0) {
    return null;
  }

  const impliedTotal = unitPrice * quantity;
  const amount = Math.abs(impliedTotal - price);
  if (amount < MISMATCH_ABSOLUTE_THRESHOLD || amount / price < MISMATCH_RELATIVE_THRESHOLD) {
    return null;
  }

  return {
    price,
    impliedTotal,
    unitPrice,
    quantity,
    amount,
    percent: (amount / price) * 100,
  };
}

/**
 * @param {object[]} scanRows Compact `price_history` rows (all verified states).
 * @param {{ humanVerifiedView?: "verified" | "unverified" | "both" }} [options]
 * @returns {Array<{
 *   id: string,
 *   kinds: Array<"week" | "month" | "mismatch">,
 *   week?: object,
 *   month?: object,
 *   mismatch?: object,
 * }>}
 */
export function detectVerifyPriceAnomalies(scanRows, options = {}) {
  const view = options.humanVerifiedView || "unverified";
  const averages = buildProductWindowAverages(scanRows);
  const findings = [];

  for (const row of scanRows || []) {
    if (!row?.id || !matchesVerifiedView(row, view)) continue;

    const kinds = [];
    const finding = { id: String(row.id), kinds };
    const productId = productIdFromRow(row);
    const unitPrice = toFiniteNumber(row.unit_price ?? row.unitPriceValue);
    const recordAt = row.record_at ?? row.recordAtValue;
    const weekKey = weekKeyFromDate(recordAt);
    const monthKey = monthKeyFromDate(recordAt);

    if (productId && unitPrice != null) {
      const weekStats = averages.weeks.get(`${productId}|${weekKey}`);
      const week = deviationCheck(unitPrice, weekStats, MIN_WEEK_SAMPLE, DEVIATION_THRESHOLD);
      if (week) {
        kinds.push("week");
        finding.week = { ...week, bucket: weekKey };
      }

      const monthStats = averages.months.get(`${productId}|${monthKey}`);
      const month = deviationCheck(unitPrice, monthStats, MIN_MONTH_SAMPLE, DEVIATION_THRESHOLD);
      if (month) {
        kinds.push("month");
        finding.month = { ...month, bucket: monthKey };
      }
    }

    const mismatch = mismatchCheck(row);
    if (mismatch) {
      kinds.push("mismatch");
      finding.mismatch = mismatch;
    }

    if (kinds.length) {
      findings.push(finding);
    }
  }

  return findings;
}

export function summarizeAnomalies(findings) {
  const summary = { total: 0, week: 0, month: 0, mismatch: 0 };
  for (const finding of findings || []) {
    summary.total += 1;
    if (finding.kinds?.includes("week")) summary.week += 1;
    if (finding.kinds?.includes("month")) summary.month += 1;
    if (finding.kinds?.includes("mismatch")) summary.mismatch += 1;
  }
  return summary;
}

function pluralize(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function formatAnomalySummaryMessage(summary) {
  if (!summary?.total) {
    return "No anomalies found for the current filters.";
  }

  const parts = [];
  if (summary.week) {
    parts.push(pluralize(summary.week, "weekly deviation", "weekly deviations"));
  }
  if (summary.month) {
    parts.push(pluralize(summary.month, "monthly deviation", "monthly deviations"));
  }
  if (summary.mismatch) {
    parts.push(pluralize(summary.mismatch, "unit/total mismatch", "unit/total mismatches"));
  }

  const prefix = pluralize(summary.total, "record", "records");
  return `Found ${prefix} with anomalies (${parts.join(", ")}).`;
}

function formatSignedPercent(check) {
  const sign = check.above ? "+" : "−";
  return `${sign}${Math.round(check.percent)}% vs avg`;
}

export function formatAnomalyChips(anomaly) {
  const chips = [];
  if (!anomaly) return chips;
  if (anomaly.week) {
    chips.push({ value: `Week ${formatSignedPercent(anomaly.week)}` });
  }
  if (anomaly.month) {
    chips.push({ value: `Month ${formatSignedPercent(anomaly.month)}` });
  }
  if (anomaly.mismatch) {
    chips.push({ value: "Unit ≠ total" });
  }
  return chips;
}

/**
 * @param {object | null | undefined} anomaly
 * @param {(value: number) => string} formatCurrency
 */
export function formatAnomalyAlertLines(anomaly, formatCurrency) {
  const format = typeof formatCurrency === "function" ? formatCurrency : String;
  const lines = [];

  if (anomaly?.week) {
    const direction = anomaly.week.above ? "above" : "below";
    lines.push(
      `Weekly unit price is ${Math.round(anomaly.week.percent)}% ${direction} this product's week average (${format(
        anomaly.week.avg
      )}).`
    );
  }
  if (anomaly?.month) {
    const direction = anomaly.month.above ? "above" : "below";
    lines.push(
      `Monthly unit price is ${Math.round(anomaly.month.percent)}% ${direction} this product's month average (${format(
        anomaly.month.avg
      )}).`
    );
  }
  if (anomaly?.mismatch) {
    lines.push(
      `Unit price × weight (${format(anomaly.mismatch.impliedTotal)}) does not match the total price (${format(
        anomaly.mismatch.price
      )}).`
    );
  }

  return lines;
}
